/**
 * The account-level defaults, including the retry policy everything inherits from.
 *
 * The three numbers at the top are the ones that matter most, and the reason they
 * exist at all is a specific failure: a call that reaches voicemail can reach
 * voicemail every single time, so without a cap this app would ring the same number
 * forever. The precedence is stated on screen — the outcome's own override, then
 * these — because "I set 30 minutes and it retried after 2 hours" is otherwise a
 * mystery spread across two tabs. There are exactly two levels: routing has no
 * override for either number, and saying otherwise sent people to a third tab looking
 * for a setting that was deliberately deleted.
 *
 * The outcome-verification numbers answer a question worth being explicit about: how
 * we know whether anybody picked up. We ask 8loop. After a call is placed the
 * callback waits in "awaiting outcome" and we poll `GET /calls/{id}` for its
 * `end_reason` — first after the delay below, then on the interval, up to the cap.
 * If the tenant's own webhook reaches us first we use that to pull the next check
 * forward, but we do not depend on it: it needs the FDE to route callback-originated
 * calls back to us, and `end_reason` is 8loop's own field with the same meaning on
 * every tenant.
 */

import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { SettingsSection } from "@/components/ui/settings-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ApiError, errorMessage, type Account, type AccountConfig } from "@/lib/api";
import { minutesToWords } from "@/lib/labels";
import { useUpdateAccount } from "@/lib/queries";

/** Only the fields this screen edits — everything else on the account is left alone. */
type Editable = Pick<
  Account,
  | "retry_delay_minutes"
  | "max_attempts_per_channel"
  | "attempt_lookback_days"
  | "no_time_delay_minutes"
  | "default_hour"
  | "min_lead_minutes"
  | "max_defer_days"
  | "max_horizon_days"
  | "verify_first_delay_seconds"
  | "verify_interval_seconds"
  | "verify_max_checks"
  | "daily_dial_cap"
  | "paused"
  | "timezone"
  | "default_country_code"
>;

/** The fields `Num` is allowed to edit — everything on `Editable` holding a number. */
type NumericKey = {
  [K in keyof Editable]: Editable[K] extends number | null ? K : never;
}[keyof Editable];

const pick = (a: Account): Editable => ({
  retry_delay_minutes: a.retry_delay_minutes,
  max_attempts_per_channel: a.max_attempts_per_channel,
  attempt_lookback_days: a.attempt_lookback_days,
  no_time_delay_minutes: a.no_time_delay_minutes,
  default_hour: a.default_hour,
  min_lead_minutes: a.min_lead_minutes,
  max_defer_days: a.max_defer_days,
  max_horizon_days: a.max_horizon_days,
  verify_first_delay_seconds: a.verify_first_delay_seconds,
  verify_interval_seconds: a.verify_interval_seconds,
  verify_max_checks: a.verify_max_checks,
  daily_dial_cap: a.daily_dial_cap,
  paused: a.paused,
  timezone: a.timezone,
  default_country_code: a.default_country_code,
});

export function DefaultsSection({
  accountId,
  config,
}: {
  accountId: string;
  config: AccountConfig;
}) {
  const update = useUpdateAccount(accountId);
  const [form, setForm] = useState<Editable>(() => pick(config.account));
  const [dirty, setDirty] = useState(false);
  /*
    Per-field validation errors from the server, keyed by the field name it sent.

    This tab edits nineteen fields behind one Save button, and a 422 used to arrive as
    a single toast — "timezone: Unknown time zone" floating above nineteen inputs, gone
    in four seconds, with nothing marking which box was wrong. The server already tells
    us exactly which field failed (`ApiError.errors`, built from the zod issue path in
    server/src/http/app.ts), so the message belongs under that input.
  */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!dirty) setForm(pick(config.account));
  }, [config.account, dirty]);

  const set = <K extends keyof Editable>(key: K, value: Editable[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    // Editing a rejected field clears its complaint — leaving it there while the
    // value changes underneath makes the error look like it belongs to the new value.
    setFieldErrors((e) => (key in e ? omit(e, key) : e));
  };

  /**
   * Numeric setter for `Num`, which is generic over the field it edits.
   *
   * The cast is here and nowhere else: TypeScript cannot prove `number | null`
   * satisfies `Editable[K]` for an unresolved `K`, even though `NumericKey` admits
   * only the fields where it does.
   */
  const setNum = (key: NumericKey, value: number | null) =>
    set(key, value as Editable[NumericKey]);

  /**
   * One save bar for the whole tab, pinned to the bottom while there is something
   * to save.
   *
   * All four sections below edit the same account row, so a Save button per
   * section would be four buttons that do the identical thing — which reads as
   * four independent forms and invites people to hunt for "the right one".
   */
  const saveBar = dirty ? (
    <div className="sticky bottom-0 z-10 -mb-2 flex items-center gap-3 rounded-lg border border-border bg-background/95 px-4 py-3 shadow-lg backdrop-blur">
      <p className="mr-auto text-xs text-muted-foreground">Unsaved changes on this tab.</p>
      <Button
        variant="ghost"
        size="sm"
        disabled={update.isPending}
        onClick={() => {
          setForm(pick(config.account));
          setDirty(false);
          setFieldErrors({});
        }}
      >
        Discard
      </Button>
      <Button
        size="sm"
        className="min-w-[120px]"
        disabled={update.isPending}
        onClick={() =>
          update.mutate(form as unknown as Record<string, unknown>, {
            onSuccess: () => {
              setDirty(false);
              setFieldErrors({});
              toast.success("Saved.");
            },
            onError: (e) => {
              const byField =
                e instanceof ApiError
                  ? Object.fromEntries(e.errors.map((f) => [f.field, f.message]))
                  : {};
              setFieldErrors(byField);
              // The toast still fires, but it counts rather than repeats: the detail is
              // now pinned to the inputs, which do not disappear after four seconds.
              const n = Object.keys(byField).length;
              toast.error(
                n > 0
                  ? `Not saved — ${n} field${n === 1 ? "" : "s"} rejected. See the notes below.`
                  : errorMessage(e),
              );
            },
          })
        }
      >
        {update.isPending ? "Saving…" : "Save changes"}
      </Button>
    </div>
  ) : null;

  return (
    <>
      <SettingsSection
        title="Retries"
        description="What happens when nobody picks up. An outcome can override either number on the Outcomes tab; this is what it inherits when it doesn't."
      >
        <Num
          label="Retry after"
          unit="minutes"
          value={form.retry_delay_minutes}
          min={1}
          onChange={(v) => setNum("retry_delay_minutes", v)}
          error={fieldErrors.retry_delay_minutes}
          hint={`Currently ${minutesToWords(form.retry_delay_minutes)}. Applies after a call that went to voicemail or was not answered.`}
        />
        <Num
          label="Attempt cap"
          unit="dials per number per agent channel"
          value={form.max_attempts_per_channel}
          min={1}
          max={10}
          onChange={(v) => setNum("max_attempts_per_channel", v)}
          error={fieldErrors.max_attempts_per_channel}
          hint="Once a number has been dialled this many times on one channel, it is suppressed for that channel instead of being retried again. Without this, a number that always reaches voicemail would be called forever."
        />
        <Num
          label="Count attempts over"
          unit="days"
          value={form.attempt_lookback_days}
          min={1}
          onChange={(v) => setNum("attempt_lookback_days", v)}
          error={fieldErrors.attempt_lookback_days}
          hint="How far back the cap looks. A number that hit the cap six months ago is callable again."
        />

        {/* Was: "the outcome's own override, then the matched route's override, then
            these" — a three-level precedence describing the rules engine that no longer
            exists. Routing is identity now and carries no retry override at all, so the
            only two levels are the ones named here. */}
        <Alert>
          <AlertDescription className="text-xs">
            Two levels, and only two: an outcome's own override on the Outcomes tab, else these.
            Routing has no say in either number. Each callback's detail panel names which of the
            two supplied its numbers.
          </AlertDescription>
        </Alert>
      </SettingsSection>

      <SettingsSection
        title="When no time was asked for"
        description="A voicemail leaves nobody to name a time, so these decide it."
      >
        <Num
          label="Call after"
          unit="minutes"
          value={form.no_time_delay_minutes}
          min={0}
          onChange={(v) => setNum("no_time_delay_minutes", v)}
          error={fieldErrors.no_time_delay_minutes}
          hint={`${minutesToWords(form.no_time_delay_minutes)} after the conversation ended, moved into calling hours if it lands outside them.`}
        />
        <Num
          label="Default hour of day"
          unit="0–23, this account's timezone"
          value={form.default_hour}
          min={0}
          max={23}
          onChange={(v) => setNum("default_hour", v)}
          error={fieldErrors.default_hour}
          hint="Used when a date was given with no time — “call me tomorrow”."
        />
        <Num
          label="Minimum lead time"
          unit="minutes"
          value={form.min_lead_minutes}
          min={0}
          onChange={(v) => setNum("min_lead_minutes", v)}
          error={fieldErrors.min_lead_minutes}
          hint="Never dial sooner than this after ingest, even if the time asked for has already passed."
        />
        <Num
          label="Never push a callback more than"
          unit="days"
          value={form.max_defer_days}
          min={1}
          onChange={(v) => setNum("max_defer_days", v)}
          error={fieldErrors.max_defer_days}
          hint="A callback that cannot find an open window within this many days goes to Needs attention rather than drifting."
        />
        <Num
          label="Refuse times further out than"
          unit="days"
          value={form.max_horizon_days}
          min={1}
          onChange={(v) => setNum("max_horizon_days", v)}
          error={fieldErrors.max_horizon_days}
          hint="A requested time beyond this is almost always a parsing mistake, so it is flagged instead of queued."
        />
      </SettingsSection>

      <SettingsSection
        title="How we find out whether the call was answered"
        description="After placing a call we ask 8loop how it ended, on a timer, until it tells us."
      >
        <Num
          label="First check after"
          unit="seconds"
          value={form.verify_first_delay_seconds}
          min={15}
          max={3600}
          onChange={(v) => setNum("verify_first_delay_seconds", v)}
          error={fieldErrors.verify_first_delay_seconds}
          hint="Long enough that a short call has finished and 8loop has settled on an end reason."
        />
        <Num
          label="Then check every"
          unit="seconds"
          value={form.verify_interval_seconds}
          min={15}
          max={3600}
          onChange={(v) => setNum("verify_interval_seconds", v)}
          error={fieldErrors.verify_interval_seconds}
          hint="Only for calls still in progress. Polling costs nothing against the dial rate limit."
        />
        <Num
          label="Give up after"
          unit="checks"
          value={form.verify_max_checks}
          min={1}
          max={200}
          onChange={(v) => setNum("verify_max_checks", v)}
          error={fieldErrors.verify_max_checks}
          hint="A call still not resolved by then goes to Needs attention. We never guess an outcome — guessing wrong either rings someone who already spoke to us or drops someone who never did."
        />
      </SettingsSection>

      {/*
        Arun, of this card: *"do we need this if we have the pgone number already and the agent
        channel etc?"* Yes, and the card has to say why rather than making somebody work it out.
        Neither value is derivable from 8loop: the agent channel gives the number this app dials
        *from*, not where the customer is, and no 8loop object this app reads carries the
        tenant's timezone. Without them a callback for "4:30" has no meaning and a number posted
        as "9568507001" cannot be dialled.
      */}
      <SettingsSection
        title="Where this account's customers are"
        description="Two things 8loop does not tell us, and the switch that stops every call."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="timezone">Timezone</Label>
            <Input
              id="timezone"
              value={form.timezone}
              aria-invalid={fieldErrors.timezone ? true : undefined}
              onChange={(e) => set("timezone", e.target.value)}
            />
            {/* The likeliest 422 on this tab by a distance — a typo in an IANA name is
                invisible until the server rejects it. */}
            {fieldErrors.timezone && (
              <p className="text-xs font-medium text-destructive">{fieldErrors.timezone}</p>
            )}
            <p className="text-xs text-muted-foreground">
              IANA name, e.g. Asia/Kolkata. It decides what “4:30” means, when your calling hours
              open and close, and every time shown in this app. 8loop does not expose a tenant's
              timezone, so it is set here.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc">Default country code</Label>
            <Input
              id="cc"
              value={form.default_country_code ?? ""}
              placeholder="+91"
              aria-invalid={fieldErrors.default_country_code ? true : undefined}
              onChange={(e) => set("default_country_code", e.target.value || null)}
            />
            {fieldErrors.default_country_code && (
              <p className="text-xs font-medium text-destructive">
                {fieldErrors.default_country_code}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Applied to a local number posted without one — “9568507001” becomes “+919568507001”,
              which is the only form 8loop will dial. The agent channel is the number you call{" "}
              <em>from</em>, so it cannot answer this.
            </p>
          </div>
        </div>

        <Num
          label="Daily dial cap"
          unit="calls per day, blank for none"
          value={form.daily_dial_cap}
          min={1}
          nullable
          onChange={(v) => setNum("daily_dial_cap", v)}
          error={fieldErrors.daily_dial_cap}
          hint="A safety valve for a misconfigured hook. Callbacks over the cap wait for tomorrow rather than being dropped."
        />

        <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
          <div>
            {/* Same field as the Pause button in the queue header, named the same
                way on purpose. It stays here because everything else on this tab is
                account defaults, but the queue is where you reach for it — and two
                controls with different names for one column is how someone ends up
                believing "Pause this account" and "Callbacks active" are separate
                settings that disagree. */}
            {/* `htmlFor` so the sentence is a hit target for the switch beside it. The
                switch is a 36×20 rectangle at the far right of a 700px row; the words
                naming it were not clickable, which is a long way to travel for a control
                whose label was sitting right there. */}
            <Label htmlFor="paused" className="text-sm">
              Pause callbacks
            </Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              First calls and retries both stop. Ingest keeps recording and scheduling, so nothing
              is lost — the queue just stops draining. Use this rather than deleting routes when a
              customer asks you to stop. Also on the Callback queue, top right.
            </p>
          </div>
          <Switch
            id="paused"
            checked={form.paused}
            onCheckedChange={(v) => set("paused", v)}
          />
        </div>
      </SettingsSection>

      {saveBar}
    </>
  );
}

/** `{...rest}` for one key, without mutating the original. */
function omit<T extends object, K extends keyof T>(obj: T, key: K): Omit<T, K> {
  const { [key]: _dropped, ...rest } = obj;
  return rest;
}

function Num({
  label,
  unit,
  hint,
  value,
  min,
  max,
  nullable,
  error,
  onChange,
}: {
  label: string;
  unit: string;
  hint?: string;
  value: number | null;
  min?: number;
  max?: number;
  nullable?: boolean;
  /** The server's complaint about this field, if the last save was rejected. */
  error?: string;
  onChange: (value: number | null) => void;
}) {
  /*
    A real id, so the visible text is actually this input's label.

    All fourteen of these were bare `<Label>`s with no `htmlFor`, which looks identical
    and behaves differently: clicking "Attempt cap" did nothing instead of focusing its
    box, and the label was invisible to assistive tech — the `aria-label` below was
    carrying the whole load, and it reads as one long parenthetical instead.
  */
  const id = useId();
  return (
    <div className="grid gap-2 sm:grid-cols-[260px_1fr] sm:items-start sm:gap-4">
      <div>
        <Label htmlFor={id} className="text-sm">
          {label}
        </Label>
        <p className="mt-0.5 text-xs text-muted-foreground">{unit}</p>
      </div>
      <div className="min-w-0 space-y-1">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          className="h-9 max-w-[160px]"
          aria-label={`${label} (${unit})`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          value={value ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            // Clearing a required field would send `null` to a NOT NULL column, so an
            // empty box keeps the current number instead of inventing one.
            if (raw === "") onChange(nullable ? null : value);
            else onChange(Number(raw));
          }}
        />
        {error && (
          <p id={`${id}-error`} className="text-xs font-medium text-destructive">
            {error}
          </p>
        )}
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}
